/**
 * CompanyInfoStep Component
 * =========================
 * Collects company/business information for KYB verification.
 * Fields: legal_name, trading_name, registration_number, tax_id,
 * country_of_incorporation, entity_type, industry, website, address
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Globe,
  MapPin,
  FileText,
  Loader2,
} from 'lucide-react';

const ENTITY_TYPES = [
  { value: 'llc', label: 'Limited Liability Company (LLC)' },
  { value: 'corporation', label: 'Corporation (C-Corp)' },
  { value: 's_corporation', label: 'S-Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'nonprofit', label: 'Non-Profit Organization' },
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'trust', label: 'Trust' },
  { value: 'other', label: 'Other' },
];

const INDUSTRIES = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Financial Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail & E-Commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'hospitality', label: 'Hospitality & Tourism' },
  { value: 'education', label: 'Education' },
  { value: 'construction', label: 'Construction' },
  { value: 'agriculture', label: 'Agriculture' },
  { value: 'transportation', label: 'Transportation & Logistics' },
  { value: 'media', label: 'Media & Entertainment' },
  { value: 'energy', label: 'Energy & Utilities' },
  { value: 'crypto', label: 'Cryptocurrency & Blockchain' },
  { value: 'other', label: 'Other' },
];

const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'AU', label: 'Australia' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'JP', label: 'Japan' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'IE', label: 'Ireland' },
  { value: 'KY', label: 'Cayman Islands' },
  { value: 'BVI', label: 'British Virgin Islands' },
  { value: 'OTHER', label: 'Other' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

export default function CompanyInfoStep({
  config = {},
  branding = {},
  sessionData = {},
  onComplete,
  onBack,
  onError,
  apiClient,
}) {
  const [animateIn, setAnimateIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state - initialize from sessionData if resuming
  const [formData, setFormData] = useState({
    legal_name: sessionData?.company_info?.legal_name || '',
    trading_name: sessionData?.company_info?.trading_name || '',
    registration_number: sessionData?.company_info?.registration_number || '',
    tax_id: sessionData?.company_info?.tax_id || '',
    country_of_incorporation: sessionData?.company_info?.country_of_incorporation || 'US',
    entity_type: sessionData?.company_info?.entity_type || '',
    industry: sessionData?.company_info?.industry || '',
    website: sessionData?.company_info?.website || '',
    // Address fields
    address_line_1: sessionData?.company_info?.address?.line_1 || '',
    address_line_2: sessionData?.company_info?.address?.line_2 || '',
    city: sessionData?.company_info?.address?.city || '',
    state: sessionData?.company_info?.address?.state || '',
    postal_code: sessionData?.company_info?.address?.postal_code || '',
    address_country: sessionData?.company_info?.address?.country || 'US',
  });

  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required fields
    if (!formData.legal_name.trim()) {
      newErrors.legal_name = 'Legal company name is required';
    }
    if (!formData.country_of_incorporation) {
      newErrors.country_of_incorporation = 'Country of incorporation is required';
    }
    if (!formData.entity_type) {
      newErrors.entity_type = 'Entity type is required';
    }
    if (!formData.industry) {
      newErrors.industry = 'Industry is required';
    }

    // Registration number validation (if provided)
    if (formData.registration_number && formData.registration_number.length < 3) {
      newErrors.registration_number = 'Invalid registration number';
    }

    // Website validation (if provided)
    if (formData.website) {
      const websiteRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!websiteRegex.test(formData.website)) {
        newErrors.website = 'Invalid website URL';
      }
    }

    // Address validation
    if (!formData.address_line_1.trim()) {
      newErrors.address_line_1 = 'Street address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.postal_code.trim()) {
      newErrors.postal_code = 'Postal code is required';
    }
    if (formData.address_country === 'US' && !formData.state) {
      newErrors.state = 'State is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // Format data for API
      const companyData = {
        legal_name: formData.legal_name.trim(),
        trading_name: formData.trading_name.trim() || null,
        registration_number: formData.registration_number.trim() || null,
        tax_id: formData.tax_id.trim() || null,
        country_of_incorporation: formData.country_of_incorporation,
        entity_type: formData.entity_type,
        industry: formData.industry,
        website: formData.website.trim() || null,
        address: {
          line_1: formData.address_line_1.trim(),
          line_2: formData.address_line_2.trim() || null,
          city: formData.city.trim(),
          state: formData.state || null,
          postal_code: formData.postal_code.trim(),
          country: formData.address_country,
        },
      };

      // Submit to API if client available
      if (apiClient?.completeStep) {
        await apiClient.completeStep('company_info', companyData);
      }

      onComplete({ company_info: companyData });
    } catch (err) {
      console.error('Failed to submit company info:', err);
      if (onError) {
        onError(err);
      }
      setErrors({ submit: err.message || 'Failed to save company information' });
    } finally {
      setSubmitting(false);
    }
  };

  // Config-driven required fields
  const requiredFields = config?.required_fields || [
    'legal_name', 'country_of_incorporation', 'entity_type', 'industry', 'address'
  ];

  const isRequired = (field) => requiredFields.includes(field);

  return (
    <div className={`sdk-step company-info-step ${animateIn ? 'animate-in' : ''}`}>
      <div className="step-header">
        {onBack && (
          <button className="back-button" onClick={onBack} type="button">
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="step-icon">
          <Building2 size={24} />
        </div>
      </div>

      <h2 className="step-title">Company Information</h2>
      <p className="step-description">
        Please provide details about your business entity.
      </p>

      <div className="form-sections">
        {/* Basic Company Info */}
        <div className="form-section">
          <h3 className="section-title">
            <FileText size={16} />
            Business Details
          </h3>

          <div className="form-group">
            <label className="form-label">
              Legal Company Name {isRequired('legal_name') && <span className="required">*</span>}
            </label>
            <input
              type="text"
              className={`form-input ${errors.legal_name ? 'error' : ''}`}
              value={formData.legal_name}
              onChange={(e) => handleChange('legal_name', e.target.value)}
              placeholder="Acme Corporation Inc."
            />
            {errors.legal_name && (
              <span className="error-message">
                <AlertCircle size={12} />
                {errors.legal_name}
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Trading Name / DBA
              <span className="optional">(optional)</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.trading_name}
              onChange={(e) => handleChange('trading_name', e.target.value)}
              placeholder="Acme"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Entity Type {isRequired('entity_type') && <span className="required">*</span>}
              </label>
              <select
                className={`form-select ${errors.entity_type ? 'error' : ''}`}
                value={formData.entity_type}
                onChange={(e) => handleChange('entity_type', e.target.value)}
              >
                <option value="">Select type...</option>
                {ENTITY_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {errors.entity_type && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {errors.entity_type}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Industry {isRequired('industry') && <span className="required">*</span>}
              </label>
              <select
                className={`form-select ${errors.industry ? 'error' : ''}`}
                value={formData.industry}
                onChange={(e) => handleChange('industry', e.target.value)}
              >
                <option value="">Select industry...</option>
                {INDUSTRIES.map(ind => (
                  <option key={ind.value} value={ind.value}>{ind.label}</option>
                ))}
              </select>
              {errors.industry && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {errors.industry}
                </span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Country of Incorporation {isRequired('country_of_incorporation') && <span className="required">*</span>}
            </label>
            <select
              className={`form-select ${errors.country_of_incorporation ? 'error' : ''}`}
              value={formData.country_of_incorporation}
              onChange={(e) => handleChange('country_of_incorporation', e.target.value)}
            >
              {COUNTRIES.map(country => (
                <option key={country.value} value={country.value}>{country.label}</option>
              ))}
            </select>
            {errors.country_of_incorporation && (
              <span className="error-message">
                <AlertCircle size={12} />
                {errors.country_of_incorporation}
              </span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Registration Number
                <span className="optional">(optional)</span>
              </label>
              <input
                type="text"
                className={`form-input ${errors.registration_number ? 'error' : ''}`}
                value={formData.registration_number}
                onChange={(e) => handleChange('registration_number', e.target.value)}
                placeholder="e.g., EIN, Company Number"
              />
              {errors.registration_number && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {errors.registration_number}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Tax ID
                <span className="optional">(optional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.tax_id}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                placeholder="XX-XXXXXXX"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <Globe size={14} />
              Website
              <span className="optional">(optional)</span>
            </label>
            <input
              type="url"
              className={`form-input ${errors.website ? 'error' : ''}`}
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              placeholder="https://example.com"
            />
            {errors.website && (
              <span className="error-message">
                <AlertCircle size={12} />
                {errors.website}
              </span>
            )}
          </div>
        </div>

        {/* Business Address */}
        <div className="form-section">
          <h3 className="section-title">
            <MapPin size={16} />
            Business Address
          </h3>

          <div className="form-group">
            <label className="form-label">
              Street Address {isRequired('address') && <span className="required">*</span>}
            </label>
            <input
              type="text"
              className={`form-input ${errors.address_line_1 ? 'error' : ''}`}
              value={formData.address_line_1}
              onChange={(e) => handleChange('address_line_1', e.target.value)}
              placeholder="123 Main Street"
            />
            {errors.address_line_1 && (
              <span className="error-message">
                <AlertCircle size={12} />
                {errors.address_line_1}
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Address Line 2
              <span className="optional">(optional)</span>
            </label>
            <input
              type="text"
              className="form-input"
              value={formData.address_line_2}
              onChange={(e) => handleChange('address_line_2', e.target.value)}
              placeholder="Suite 100"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                City {isRequired('address') && <span className="required">*</span>}
              </label>
              <input
                type="text"
                className={`form-input ${errors.city ? 'error' : ''}`}
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                placeholder="San Francisco"
              />
              {errors.city && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {errors.city}
                </span>
              )}
            </div>

            {formData.address_country === 'US' ? (
              <div className="form-group">
                <label className="form-label">
                  State {isRequired('address') && <span className="required">*</span>}
                </label>
                <select
                  className={`form-select ${errors.state ? 'error' : ''}`}
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                >
                  <option value="">Select state...</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {errors.state && (
                  <span className="error-message">
                    <AlertCircle size={12} />
                    {errors.state}
                  </span>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">
                  State / Province
                  <span className="optional">(optional)</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  placeholder="Province / Region"
                />
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Postal Code {isRequired('address') && <span className="required">*</span>}
              </label>
              <input
                type="text"
                className={`form-input ${errors.postal_code ? 'error' : ''}`}
                value={formData.postal_code}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="94102"
              />
              {errors.postal_code && (
                <span className="error-message">
                  <AlertCircle size={12} />
                  {errors.postal_code}
                </span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Country</label>
              <select
                className="form-select"
                value={formData.address_country}
                onChange={(e) => handleChange('address_country', e.target.value)}
              >
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>{country.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {errors.submit && (
        <div className="submit-error">
          <AlertCircle size={16} />
          {errors.submit}
        </div>
      )}

      <div className="step-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="spin" />
              Saving...
            </>
          ) : (
            <>
              Continue
              <ChevronRight size={18} />
            </>
          )}
        </button>
      </div>

      <style>{companyInfoStyles}</style>
    </div>
  );
}

const companyInfoStyles = `
  .company-info-step {
    padding: 24px;
    max-width: 560px;
    margin: 0 auto;
  }

  .step-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }

  .back-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    color: var(--sdk-text-secondary, #6B7280);
    border-radius: 8px;
    transition: all 0.2s;
  }

  .back-button:hover {
    background: var(--sdk-bg-secondary, #F3F4F6);
    color: var(--sdk-text, #1F2937);
  }

  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .step-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
    margin: 0 0 8px;
  }

  .step-description {
    color: var(--sdk-text-secondary, #6B7280);
    font-size: 15px;
    margin: 0 0 24px;
    line-height: 1.5;
  }

  .form-sections {
    display: flex;
    flex-direction: column;
    gap: 28px;
  }

  .form-section {
    background: var(--sdk-bg-secondary, #F9FAFB);
    border-radius: 16px;
    padding: 20px;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--sdk-text, #1F2937);
    margin: 0 0 16px;
  }

  .section-title svg {
    color: var(--sdk-primary, #0D9488);
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-group:last-child {
    margin-bottom: 0;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  @media (max-width: 480px) {
    .form-row {
      grid-template-columns: 1fr;
    }
  }

  .form-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--sdk-text, #374151);
    margin-bottom: 6px;
  }

  .form-label svg {
    color: var(--sdk-text-secondary, #6B7280);
  }

  .form-label .required {
    color: #EF4444;
  }

  .form-label .optional {
    font-weight: 400;
    color: var(--sdk-text-secondary, #9CA3AF);
    margin-left: 4px;
  }

  .form-input,
  .form-select {
    width: 100%;
    padding: 12px 14px;
    font-size: 14px;
    border: 2px solid var(--sdk-border, #E5E7EB);
    border-radius: 10px;
    background: white;
    color: var(--sdk-text, #1F2937);
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }

  .form-input:focus,
  .form-select:focus {
    border-color: var(--sdk-primary, #0D9488);
    box-shadow: 0 0 0 3px rgba(var(--sdk-primary-rgb, 13, 148, 136), 0.1);
  }

  .form-input.error,
  .form-select.error {
    border-color: #EF4444;
  }

  .form-input::placeholder {
    color: var(--sdk-text-secondary, #9CA3AF);
  }

  .form-select {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 40px;
  }

  .error-message {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #EF4444;
    margin-top: 6px;
  }

  .submit-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 10px;
    color: #DC2626;
    font-size: 14px;
    margin-top: 20px;
  }

  .step-actions {
    margin-top: 28px;
  }

  .primary-button {
    width: 100%;
    padding: 14px 24px;
    background: var(--sdk-primary, #0D9488);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;
  }

  .primary-button:hover:not(:disabled) {
    background: var(--sdk-primary-dark, #0F766E);
    transform: translateY(-1px);
  }

  .primary-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
